import i18n from '@/utils/i18n';
import validator from 'validator';
import {
  AddressInput,
  getAddressInputFromPlaceResult,
} from '@/components/addresses/address-input';
import { AddressInputType } from '@domain/addresses/types/types';
import { AUTH_MODAL_ROUTES, CONTAINER_MODAL_ROUTES } from '@/navigators/routes';
import {
  AuthModalStackParams,
  ContainerModalStackParams,
} from '@/navigators/types';
import { Button } from '@/components/button';
import { CompositeNavigationProp } from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { EmailInput } from '@/components/email-input';
import { FormInputContainer } from '@/components/form';
import { KeyboardAwareScrollView } from '@/components/with-keyboard-aware';
import { ModalContainer } from '@/components/modal-container';
import { PhoneNumberInput } from '@/components/phone-number-input';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { StackNavigationProp } from '@react-navigation/stack';
import { Text, View } from 'react-native';
import { TextInput } from '@/components/text-input';
import { useRecoverLogin } from '@/hooks/use-recover-login';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useState } from 'react';
import { useToasts } from '@/contexts/toast-context';

export type Navigation = CompositeNavigationProp<
  StackNavigationProp<AuthModalStackParams, AUTH_MODAL_ROUTES.LoginRecovery>,
  StackNavigationProp<
    ContainerModalStackParams,
    CONTAINER_MODAL_ROUTES.PlacesSearch
  >
>;

interface Props {
  navigation: Navigation;
}

export const LoginRecoveryScreen: React.FC<Props> = ({ navigation }) => {
  const { bottom } = useSafeAreaInsets();
  const { isSubmitting, recoverLoginCallback } = useRecoverLogin();
  const { showErrorToast } = useToasts();
  const [styles, theme] = useStyles();

  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] =
    useState<AddressInputType | null>(null);
  const [businessAddress2, setBusinessAddress2] = useState('');
  const [accountID, setAccountId] = useState('');
  const [hasEmailError, setHasEmailError] = useState(false);
  const [hasPhoneError, setHasPhoneError] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const form = (
    <KeyboardAwareScrollView
      contentContainerStyle={[
        styles.formContentContainer,
        { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
      ]}
      extraSpacing={40}
      keyboardDismissMode="on-drag"
    >
      <View style={styles.inputFieldsContainer}>
        <FormInputContainer>
          <TextInput
            containerStyle={styles.inputFieldContainer}
            focusedBorderColor={theme.colors.accent}
            label={businessName ? `${i18n.t('business_name')}*` : null}
            onChangeText={setBusinessName}
            placeholder={`${i18n.t('business_name')}*`}
            testID="business-name-input"
            value={businessName}
          />
        </FormInputContainer>
        <FormInputContainer>
          <AddressInput
            address2={businessAddress2}
            address2Props={{
              containerStyle: styles.inputFieldContainer,
              testID: 'business-address-2-input',
            }}
            addressProps={{
              containerStyle: [styles.inputFieldContainer, styles.marginBottom],
              testID: 'business-address-input',
            }}
            label={i18n.t('business_address')}
            onAddress2Change={setBusinessAddress2}
            onOpenSearch={() => {
              navigation.navigate(CONTAINER_MODAL_ROUTES.PlacesSearch, {
                initialValue: businessAddress?.address || '',
                onSelect: (
                  _,
                  placeResult: google.maps.places.PlaceResult | null,
                ) => {
                  if (placeResult) {
                    setBusinessAddress(
                      getAddressInputFromPlaceResult(
                        placeResult,
                        businessAddress2,
                      ),
                    );
                  }
                },
                placeholder: i18n.t('business_address'),
                types: ['address'],
              });
            }}
            value={businessAddress}
          />
        </FormInputContainer>
        <FormInputContainer>
          <TextInput
            containerStyle={styles.inputFieldContainer}
            focusedBorderColor={theme.colors.accent}
            label={accountID ? i18n.t('account_id') : null}
            onChangeText={setAccountId}
            placeholder={i18n.t('account_id')}
            testID="account-id-input"
            value={accountID}
          />
        </FormInputContainer>
        <FormInputContainer>
          <TextInput
            containerStyle={styles.inputFieldContainer}
            focusedBorderColor={theme.colors.accent}
            label={name ? `${i18n.t('contact_name')}*` : null}
            onChangeText={setName}
            placeholder={`${i18n.t('contact_name')}*`}
            testID="name-input"
            value={name}
          />
        </FormInputContainer>
        <FormInputContainer>
          <PhoneNumberInput
            containerStyle={styles.inputFieldContainer}
            error={hasPhoneError ? i18n.t('toast.invalid_phone_number') : null}
            focusedBorderColor={theme.colors.accent}
            label={phoneNumber ? `${i18n.t('phone_number')}*` : null}
            onChangeText={(text) => {
              setPhoneNumber(text);
              if (hasPhoneError) {
                setHasPhoneError(false);
              }
            }}
            placeholder={`${i18n.t('phone_number')}*`}
            testID="phone-number-input"
          />
        </FormInputContainer>
        <FormInputContainer>
          <EmailInput
            containerStyle={[styles.inputContainer]}
            error={hasEmailError ? i18n.t('toast.invalid_email') : null}
            onChangeText={setEmail}
            placeholder={`${i18n.t('email')}*`}
            testID="email-input"
            value={email}
          />
        </FormInputContainer>
      </View>
      <FormInputContainer>
        <Button
          disabled={
            !name.trim() || !businessName.trim() || (!phoneNumber && !email)
          }
          loading={isSubmitting}
          onPress={async () => {
            const trimmedEmail = email.trim();
            const trimmedAccountID = accountID.trim();
            const trimmedBusinessAddress2 = businessAddress2.trim();

            const address = trimmedBusinessAddress2
              ? `${trimmedBusinessAddress2}, ${businessAddress?.address!}`
              : businessAddress?.address!;

            if (email && !validator.isEmail(trimmedEmail)) {
              setHasEmailError(true);
              return;
            } else {
              setHasEmailError(false);
            }

            if (!phoneNumber) {
              setHasPhoneError(true);
              return;
            }

            try {
              await recoverLoginCallback({
                accountID: trimmedAccountID || undefined,
                businessAddress: address || undefined,
                businessName,
                email: trimmedEmail || undefined,
                name,
                phoneNumber,
              });

              setShowConfirmation(true);
            } catch (e) {
              showErrorToast();
            }
          }}
          testID="login-recovery-button"
          textStyle={styles.buttonText}
          trackingData={{ name: 'login_recovery.submit_button' }}
        >
          {i18n.t('submit')}
        </Button>
      </FormInputContainer>
    </KeyboardAwareScrollView>
  );

  const confirmation = (
    <View
      style={[
        styles.confirmationContainer,
        { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
      ]}
    >
      <Text style={[TYPOGRAPHY.h2, styles.confirmationText]}>
        {i18n.t('login_recovery.confirmation.title')}
      </Text>
      <Text style={[TYPOGRAPHY.p, styles.confirmationText]}>
        {i18n.t('login_recovery.confirmation.message')}
      </Text>
      <Button
        containerStyle={styles.buttonContainer}
        onPress={() => navigation.goBack()}
        testID="exit-login-recovery-button"
        trackingData={{ name: 'login_recovery.exit_button' }}
      >
        {i18n.t('exit')}
      </Button>
    </View>
  );

  return (
    <ModalContainer
      navigationHeaderProps={{ title: i18n.t('login_recovery') }}
      shouldDismissKeyboard
    >
      {showConfirmation ? confirmation : form}
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  buttonContainer: {
    paddingTop: SCREEN_PADDING,
  },
  buttonText: {
    color: theme.colors.authBackground,
  },
  confirmationContainer: {
    gap: 8,
    padding: SCREEN_PADDING,
  },
  confirmationText: {
    textAlign: 'center',
  },
  formContentContainer: {
    flexGrow: 1,
    paddingHorizontal: SCREEN_PADDING,
  },
  inputContainer: {
    borderColor: theme.colors.stroke,
  },
  inputFieldContainer: {
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderWidth: 1,
  },
  inputFieldsContainer: {
    flex: 1,
  },
  marginBottom: {
    marginBottom: 16,
  },
}));
